import React, { useState, useMemo } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, ArrowRight, Sparkles, Calendar, Users, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const COPY = {
  hero_overline: { en: "Classic enquiry · Fast", fr: "Demande express · Rapide", es: "Consulta clásica · Rápida" },
  hero_title_a: { en: "Quick booking", fr: "Demande express,", es: "Solicitud rápida," },
  hero_title_b: { en: "request.", fr: "à l'essentiel.", es: "lo esencial." },
  hero_sub: {
    en: "A short, lightweight form for guests who already know what they want. Five fields, one host on the other end — answered within 24 hours.",
    fr: "Un formulaire court et léger pour ceux qui savent déjà ce qu'ils veulent. Cinq champs, un hôte de l'autre côté — réponse sous 24 heures.",
    es: "Un formulario breve para quienes ya saben qué quieren. Cinco campos, un anfitrión al otro lado — respuesta en 24 horas.",
  },
  switch_intro: { en: "Prefer a more detailed travel request?", fr: "Préférez une demande de voyage plus détaillée ?", es: "¿Prefiere una solicitud más detallada?" },
  switch_cta: { en: "Switch to the immersive flow", fr: "Passer au parcours immersif", es: "Cambiar al recorrido inmersivo" },
  hotel: { en: "Choose a property", fr: "Choisir une maison", es: "Elegir una casa" },
  any: { en: "Any property — please advise", fr: "Indifférent — à conseiller", es: "Sin preferencia — aconséjenos" },
  check_in: { en: "Arrival", fr: "Arrivée", es: "Llegada" },
  check_out: { en: "Departure", fr: "Départ", es: "Salida" },
  guests: { en: "Guests", fr: "Voyageurs", es: "Viajeros" },
  name: { en: "Full name", fr: "Nom complet", es: "Nombre completo" },
  email: { en: "Email", fr: "E-mail", es: "Correo electrónico" },
  phone: { en: "Phone (optional)", fr: "Téléphone (optionnel)", es: "Teléfono (opcional)" },
  message: { en: "Anything to add?", fr: "Quelque chose à ajouter ?", es: "¿Algo que añadir?" },
  submit: { en: "Send request", fr: "Envoyer la demande", es: "Enviar solicitud" },
  sending: { en: "Sending…", fr: "Envoi…", es: "Enviando…" },
  success: { en: "Thank you — a host will be in touch within 24 hours.", fr: "Merci — un hôte vous répond sous 24 heures.", es: "Gracias — un anfitrión le responderá en 24 horas." },
  error: { en: "We couldn't send the request. Please try again.", fr: "Nous n'avons pas pu envoyer la demande. Réessayez.", es: "No pudimos enviar la solicitud. Inténtelo de nuevo." },
  reassure: {
    en: "No deposit · 24h response · Human reply in your language",
    fr: "Aucun acompte · Réponse 24h · Réponse humaine dans votre langue",
    es: "Sin depósito · Respuesta 24h · Respuesta humana en tu idioma",
  },
};

const inputCls = "w-full border border-[#2C2621]/15 bg-[#FDFBF7] px-4 py-3 text-[15px] text-[#2C2621] placeholder:text-[#5C5248]/45 focus:outline-none focus:border-[#C16542] transition-colors";

const BookingPageLegacy = ({ embedded = false } = {}) => {
  const { lang } = useLanguage();
  const location = useLocation();

  const initialHotel = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("hotel") || "";
  }, [location.search]);

  const [form, setForm] = useState({
    hotel: initialHotel,
    check_in: "",
    check_out: "",
    guests: 2,
    full_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error(pick({ en: "Please fill name and email.", fr: "Renseignez nom et e-mail.", es: "Completa nombre y correo." }, lang));
      return;
    }
    setLoading(true);
    try {
      const hotelObj = HOTELS.find((h) => h.slug === form.hotel);
      const checkIn = form.check_in || new Date().toISOString().slice(0, 10);
      const checkOut = form.check_out || checkIn;
      await axios.post(`${API}/multi-booking-requests`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        items: [{
          hotel: hotelObj ? hotelObj.name : (form.hotel || "Any property"),
          check_in: checkIn,
          check_out: checkOut,
          adults: Number(form.guests) || 2,
          children: 0,
          rooms: 1,
          kind: "hotel",
        }],
        message: form.message || null,
        language: lang,
        source: "classic-booking",
      });
      toast.success(pick(COPY.success, lang));
      setForm({ hotel: "", check_in: "", check_out: "", guests: 2, full_name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(pick(COPY.error, lang));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-classic-booking" className="bg-[#FDFBF7] text-[#2C2621]">

      {/* Compact hero */}
      {!embedded && (
        <section className="relative bg-[#14100E] text-[#FDFBF7] overflow-hidden" data-testid="classic-hero" data-cms-scope="classic-booking.hero">
          <img
            src="https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/dx96oxez_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg"
            alt="Grup Xaluca"
            className="ken-burns absolute inset-0 w-full h-full object-cover opacity-55" loading="lazy" decoding="async"
            data-cms-role="bg"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#14100E]/95 via-[#14100E]/65 to-[#14100E]/35" />
          <span className="film-grain" aria-hidden="true" />
          <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32">
            <p className="overline !text-[#D4A373]">{pick(COPY.hero_overline, lang)}</p>
            <h1 className="font-serif-x mt-5 text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-3xl">
              {pick(COPY.hero_title_a, lang)}{" "}
              <em className="not-italic text-[#D4A373]">{pick(COPY.hero_title_b, lang)}</em>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#FDFBF7]/85 leading-relaxed max-w-2xl">
              {pick(COPY.hero_sub, lang)}
            </p>
          </div>
        </section>
      )}

      {/* Switch to detailed flow */}
      {!embedded && (
        <div className="bg-[#F2EBE1]/40 border-y border-[#2C2621]/10" data-testid="classic-switch-banner">
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-sm text-[#5C5248] inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#A07042]" strokeWidth={1.5} />
              {pick(COPY.switch_intro, lang)}
            </p>
            <Link
              to="/booking"
              className="group inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#C16542] hover:text-[#A35133] border-b border-[#C16542]/40 hover:border-[#A35133] pb-1 transition-colors"
              data-testid="classic-switch-to-detailed"
            >
              {pick(COPY.switch_cta, lang)}
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      )}

      {/* Form */}
      <section className="py-16 md:py-24" data-testid="classic-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <form onSubmit={submit} className="space-y-5">
            <label className="flex flex-col">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2 inline-flex items-center gap-2">
                <MapPin className="w-3 h-3" strokeWidth={1.5} />
                {pick(COPY.hotel, lang)}
              </span>
              <select
                data-testid="classic-input-hotel"
                value={form.hotel}
                onChange={(e) => set("hotel", e.target.value)}
                className={inputCls}
              >
                <option value="">{pick(COPY.any, lang)}</option>
                {HOTELS.map((h) => (
                  <option key={`co-${h.slug}`} value={h.slug}>{h.name} · {h.location}</option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2 inline-flex items-center gap-2">
                  <Calendar className="w-3 h-3" strokeWidth={1.5} />
                  {pick(COPY.check_in, lang)}
                </span>
                <input type="date" data-testid="classic-input-checkin" value={form.check_in} onChange={(e) => set("check_in", e.target.value)} className={inputCls} />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2 inline-flex items-center gap-2">
                  <Calendar className="w-3 h-3" strokeWidth={1.5} />
                  {pick(COPY.check_out, lang)}
                </span>
                <input type="date" data-testid="classic-input-checkout" value={form.check_out} onChange={(e) => set("check_out", e.target.value)} className={inputCls} />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2 inline-flex items-center gap-2">
                  <Users className="w-3 h-3" strokeWidth={1.5} />
                  {pick(COPY.guests, lang)}
                </span>
                <input type="number" min="1" data-testid="classic-input-guests" value={form.guests} onChange={(e) => set("guests", e.target.value)} className={inputCls} />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2 border-t border-[#2C2621]/10">
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2">{pick(COPY.name, lang)}</span>
                <input required data-testid="classic-input-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2">{pick(COPY.email, lang)}</span>
                <input type="email" required data-testid="classic-input-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2">{pick(COPY.phone, lang)}</span>
                <input data-testid="classic-input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2">{pick(COPY.message, lang)}</span>
                <textarea
                  rows={4}
                  data-testid="classic-input-message"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className={inputCls + " resize-none"}
                  placeholder={pick({
                    en: "A short note — room preference, occasion, anything we should know.",
                    fr: "Un mot court — préférence de chambre, occasion, ce que nous devons savoir.",
                    es: "Una nota breve — preferencia de habitación, ocasión, lo que debamos saber.",
                  }, lang)}
                />
              </label>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 pt-6">
              <p className="text-xs tracking-[0.2em] uppercase text-[#5C5248]/75">{pick(COPY.reassure, lang)}</p>
              <button
                type="submit"
                disabled={loading}
                className="group inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors"
                data-testid="classic-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.5} />}
                {loading ? pick(COPY.sending, lang) : pick(COPY.submit, lang)}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BookingPageLegacy;
